import { ref, computed, readonly, inject } from "vue";
import { defineStore } from "pinia";
import { PAGE_APP_SYMBOL } from "_Main/injectionSymbols";

export const useAttachmentViewStore = defineStore("attachmentView", () => {
    // boolean values
    const dragging = ref(false);
    const aiAnalysisRunning = ref(false);
    const isAIAssistantPreview = ref(false);

    const PREVIEW_PLACEHOLDER_ID = "PREVIEW_PLACEHOLDER_ID";

    // model content
    const model = ref({});
    const app = inject(PAGE_APP_SYMBOL);
    const attachmentDownloadUrl = computed(() => model.value.AttachmentDownloadUrl);
    const attachmentContentUrl = computed(() => model.value.AttachmentContentUrl);
    const displayType = computed(() => model.value.DisplayType);
    const attachmentModel = computed(() => model.value.AttachmentModel || {});
    const aIAnalysisResponse = computed(() => attachmentModel.value.AIAnalysisResponse || {});
    const enableAttachmentAnalysis = computed(
        () =>
            isAIAssistantPreview.value ||
            (app.LoggedInRole.RolePrivileges.ProjectAdminCanUseAIAttachmentAssistants &&
                model.value.EnableAttachmentAnalysis)
    );
    const hasAttachment = computed(() => attachmentModel.value.AttachmentId != null);

    // values set from view component
    const aiResponseContent = ref({});
    const aiExecutionTime = ref(null);
    const aiResultDebugJson = ref(null);
    const noteText = ref(null);
    const promptId = ref(null);
    const lastRunPrompt = ref(null);
    const promptList = ref([]);

    // computed
    const projectId = computed(() => model.value.ProjectId);
    const projectNumber = computed(() => model.value.ProjectNumber);
    const aiSummaryResults = computed(() => aiResponseContent.value.Summary);
    const aiRequirementsAnalysis = computed(() => aiResponseContent.value.Requirements);
    const aiNoteText = computed(() => aiSummaryResults.value?.EvaluationFeedback);
    const aiExecutionTimeString = computed(() => {
        const executionTime = new Date(aiExecutionTime.value);
        return "Results from " + executionTime.toLocaleString();
    });
    const resultsOlderThanPrompt = computed(() => {
        if (isAIAssistantPreview.value || !aiSummaryResults.value || !lastRunPrompt.value) {
            return false;
        }

        const executionTimeLocal = new Date(aiExecutionTimeString.value);
        const promptExecutionTimeLocal = new Date(lastRunPrompt.value.LastChangeTimestamp?.Display);
        return executionTimeLocal < promptExecutionTimeLocal;
    });

    // load in the content of an attachment from the model
    function loadState(viewModel) {
        model.value = viewModel;
        noteText.value = attachmentModel.value.Note;
        promptList.value = model.value.Prompts;
        aiResponseContent.value = aIAnalysisResponse.value.Response?.Content || {};
        promptId.value =
            promptList.value.find((pr) => pr.PromptId === aIAnalysisResponse.value.PromptId) != null
                ? aIAnalysisResponse.value.PromptId
                : null;
        lastRunPrompt.value = model.value.LastRunPrompt;
        aiResultDebugJson.value = aIAnalysisResponse.value.Response?.Json;
        aiExecutionTime.value = aIAnalysisResponse.value.ExecutionTime;
    }

    function loadStateForPreview(viewModel, promptModel) {
        model.value = viewModel;
        promptId.value = promptModel.PromptId || PREVIEW_PLACEHOLDER_ID;
        if (promptModel.PromptId == null) {
            promptList.value = [{ PromptId: PREVIEW_PLACEHOLDER_ID, DisplayName: promptModel.DisplayName }];
        } else {
            promptList.value = [promptModel];
        }
        isAIAssistantPreview.value = true;
    }

    function updateAttachmentStatusInfo(statusInfo) {
        attachmentModel.value.Status = statusInfo.ApprovalStatus;
        attachmentModel.value.StatusChangeUser = statusInfo.StatusChangeUser;
        attachmentModel.value.StatusChangeTimestamp = statusInfo.StatusChangeTimestamp;
    }

    function setDrag(value) {
        dragging.value = value;
    }

    function updateAIAnalysis(result) {
        aiResponseContent.value = result;
        aiResultDebugJson.value = result.DebugJson;
        aiExecutionTime.value = result.ExecutionTime;
        lastRunPrompt.value = promptList.value.find((pr) => pr.PromptId === promptId.value);
    }

    function clearAIAnalysis() {
        aiResponseContent.value = {};
        aiExecutionTime.value = null;
        aiResultDebugJson.value = null;
        lastRunPrompt.value = null;
    }

    function generateAiNote() {
        noteText.value = aiNoteText.value;
    }

    function clearAttachment() {
        model.value = {};
    }

    return {
        dragging: readonly(dragging),
        enableAttachmentAnalysis: readonly(enableAttachmentAnalysis),
        attachmentDownloadUrl: readonly(attachmentDownloadUrl),
        attachmentContentUrl: readonly(attachmentContentUrl),
        displayType: readonly(displayType),
        attachmentModel: readonly(attachmentModel),
        projectId: readonly(projectId),
        projectNumber: readonly(projectNumber),
        aiSummaryResults: readonly(aiSummaryResults),
        aiRequirementsAnalysis: readonly(aiRequirementsAnalysis),
        aiExecutionTimeString: readonly(aiExecutionTimeString),
        resultsOlderThanPrompt: readonly(resultsOlderThanPrompt),
        aiResultDebugJson: readonly(aiResultDebugJson),
        promptList: readonly(promptList),
        hasAttachment: readonly(hasAttachment),
        isAIAssistantPreview: readonly(isAIAssistantPreview),
        aiAnalysisRunning: aiAnalysisRunning,
        noteText: noteText,
        promptId: promptId,
        lastRunPrompt: lastRunPrompt,
        loadState,
        loadStateForPreview,
        updateAttachmentStatusInfo,
        setDrag,
        updateAIAnalysis,
        clearAIAnalysis,
        generateAiNote,
        clearAttachment
    };
});
